import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  getCurrentUserDetailAsync,
  updateUserDetail,
  deleteUserAsync,
  requestForUserEmailUpdateAsync,
  requestForUserPhoneUpdateAsync,
} from "apis/UserApi";
import { toastSuccess } from "components/common/ToastNotification";
import UpdateCurrentUserRequest from "interfaces/request/UpdateCurrentUserRequest";
import CurrentUserResponse from "interfaces/response/CurrentUserResponse";
import UserState from "interfaces/state/UserState";
import { RootState } from "store/store";
import UserPhoneUpdateRequest from "interfaces/request/UserPhoneUpdateRequest";
import UserEmailUpdateRequest from "interfaces/request/UserEmailUpdateRequest";

const initialState: UserState = {
  currentUserDetail: null,
  loading: false,
};

export const getCurrentUserDetail = createAsyncThunk(
  "user/getCurrentUserDetail",
  async () => {
    return await getCurrentUserDetailAsync();
  }
);

export const updateUserDetailInfo = createAsyncThunk<
  CurrentUserResponse,
  { userId: number; userRequest: UpdateCurrentUserRequest },
  { state: RootState }
>("user/updateUserDetail", async (request) => {
  return await updateUserDetail(request.userId, request.userRequest);
});

export const deleteUser = createAsyncThunk(
  "user/deleteUser",
  async (userId: number) => {
    await deleteUserAsync(userId);
  }
);

export const requestToUpdateCurrentUserPhoneAsync = createAsyncThunk<
  CurrentUserResponse,
  UserPhoneUpdateRequest,
  { state: RootState }
>(
  "authentication/updateCurrentUserPhone",
  async (postData: UserPhoneUpdateRequest, thunkAPI) => {
    const state = thunkAPI.getState();
    const currentUserId = state.authData.id;
    return await requestForUserPhoneUpdateAsync(currentUserId, postData);
  }
);

export const requestToUpdateCurrentUserEmailAsync = createAsyncThunk<
  CurrentUserResponse,
  UserEmailUpdateRequest,
  { state: RootState }
>(
  "authentication/updateCurrentUserEmail",
  async (postData: UserEmailUpdateRequest, thunkAPI) => {
    const state = thunkAPI.getState();
    const currentUserId = state.authData.id;
    return await requestForUserEmailUpdateAsync(currentUserId, postData);
  }
);

export const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setCurrentUser: (state, action) => {
      state.currentUserDetail = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getCurrentUserDetail.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(getCurrentUserDetail.fulfilled, (state, action) => {
      const payload = action.payload;
      state.currentUserDetail = payload;
      state.loading = false;
    });
    builder.addCase(getCurrentUserDetail.rejected, (state, action) => {
      state.loading = false;
    });
    builder.addCase(updateUserDetailInfo.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(updateUserDetailInfo.fulfilled, (state, action) => {
      toastSuccess("User detail updated.");
      state.loading = false;
    });
    builder.addCase(updateUserDetailInfo.rejected, (state, action) => {
      state.loading = false;
    });
    builder.addCase(deleteUser.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(deleteUser.fulfilled, (state, action) => {
      state.loading = false;
    });
    builder.addCase(deleteUser.rejected, (state, action) => {
      state.loading = false;
    });
    builder.addCase(
      requestToUpdateCurrentUserPhoneAsync.pending,
      (state, action) => {
        state.loading = true;
      }
    );
    builder.addCase(
      requestToUpdateCurrentUserPhoneAsync.rejected,
      (state, action) => {
        state.loading = false;
      }
    );
    builder.addCase(
      requestToUpdateCurrentUserPhoneAsync.fulfilled,
      (state, action) => {
        if (state.currentUserDetail) {
          state.currentUserDetail.phone = action.payload.phone;
          state.currentUserDetail.isPhoneVerified =
            action.payload.isPhoneVerified;
        }
        state.loading = false;
      }
    );
    builder.addCase(
      requestToUpdateCurrentUserEmailAsync.pending,
      (state, action) => {
        state.loading = true;
      }
    );
    builder.addCase(
      requestToUpdateCurrentUserEmailAsync.rejected,
      (state, action) => {
        state.loading = false;
      }
    );
    builder.addCase(
      requestToUpdateCurrentUserEmailAsync.fulfilled,
      (state, action) => {
        if (state.currentUserDetail) {
          state.currentUserDetail.email = action.payload.email;
          state.currentUserDetail.isEmailVerified =
            action.payload.isEmailVerified;
        }
        state.loading = false;
      }
    );
  },
});

export const { setCurrentUser } = userSlice.actions;
export default userSlice.reducer;
